/**
 * usePageTitle — hook + provider per il titolo della pagina corrente.
 *
 * Uso pattern:
 *   - In `AppShell` montare <PageTitleProvider> attorno all'<Outlet />.
 *   - In ogni pagina chiamare `usePageTitle("Pazienti")` per:
 *       * settare document.title = "Pazienti · DietaGest"
 *       * fornire il titolo a AppTopBar che legge via usePageTitleValue()
 *   - L'effect cleanup riazzera document.title quando la pagina si smonta.
 *
 * Implementato con React Context (uniforme col pattern MfaStepUpProvider)
 * invece di Zustand: niente store globale persistente, niente boilerplate.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const APP_NAME = 'DietaGest';

interface PageTitleApi {
  title: string;
  setTitle: (next: string | null) => void;
}

const PageTitleContext = createContext<PageTitleApi | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitleState] = useState<string>('');

  const setTitle = useCallback((next: string | null) => {
    setTitleState(next ?? '');
    if (typeof document !== 'undefined') {
      document.title = next ? `${next} · ${APP_NAME}` : APP_NAME;
    }
  }, []);

  const api = useMemo<PageTitleApi>(
    () => ({ title, setTitle }),
    [title, setTitle]
  );

  return (
    <PageTitleContext.Provider value={api}>
      {children}
    </PageTitleContext.Provider>
  );
}

/**
 * Versione "read-only" del context, per AppTopBar e simili.
 * Ritorna stringa vuota se non c'è un provider sopra (es. pagine pubbliche).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageTitleValue(): string {
  const ctx = useContext(PageTitleContext);
  return ctx?.title ?? '';
}

/**
 * Hook da usare nelle pagine dentro AppShell. Aggiorna sia il context
 * che document.title; ripristina il titolo precedente all'unmount.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageTitle(title: string): void {
  const ctx = useContext(PageTitleContext);
  const setTitle = ctx?.setTitle;

  useEffect(() => {
    if (!setTitle) {
      if (typeof document !== 'undefined') {
        document.title = `${title} · ${APP_NAME}`;
      }
      return;
    }
    setTitle(title);
    return () => {
      setTitle(null);
    };
  }, [title, setTitle]);
}
